import React, { useState, useEffect } from "react";
import apiService from "../../services/api_services";
import authService from "../../services/auth_service";
import Modal from "react-modal";
import RatingModal from "../../components/RatingModal";
import StarRating from "../../components/StarRating";
import toast from "react-hot-toast";

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedTrackingId, setExpandedTrackingId] = useState(null);
  const [trackingData, setTrackingData] = useState({});
  const [trackingModalOrder, setTrackingModalOrder] = useState(null);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productRatings, setProductRatings] = useState({});

  const imageBaseURL = "https://newpos.spectsgenie.com/";

  let customerId;
  try {
    const { id } = authService.getAccessTokenData();
    customerId = id;
  } catch (error) {
    console.error("Failed to fetch customer ID:", error);
  }

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await apiService.fetchOrdersById(customerId);
        setOrders(response.data);
      } catch (err) {
        setError("Failed to fetch orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [customerId]);

  // Fetch tracking data when modal opens
  useEffect(() => {
    if (trackingModalOrder) {
      const fetchTrackingData = async () => {
        try {
          const response = await apiService.fetchTrackingData(
            trackingModalOrder.id,
            trackingModalOrder.order_track_id,
            trackingModalOrder.c_number
          );
          
          setTrackingData(prev => ({
            ...prev,
            [trackingModalOrder.id]: response
          }));
        } catch (error) {
          console.error("Error fetching tracking data:", error);
          setTrackingData(prev => ({
            ...prev,
            [trackingModalOrder.id]: {
              isTrackingAvailable: false,
              errorMessage: "Unable to fetch tracking information at this time."
            }
          }));
        }
      };

      if (!trackingData[trackingModalOrder.id]) {
        fetchTrackingData();
      }
    }
  }, [trackingModalOrder]);

  const toggleDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const toggleTracking = async (orderId, orderTrackId, customerNumber) => {
    if (expandedTrackingId === orderId) {
      setExpandedTrackingId(null);
      return;
    }

    setExpandedTrackingId(orderId);
    
    // If we already have tracking data for this order, don't fetch again
    if (trackingData[orderId]) {
      return;
    }

    // Fetch tracking data
    await handleTrackOrder(orderId, orderTrackId, customerNumber);
  };

  // 🔥 Function to Fetch Tracking Details from Shiprocket API
  const handleTrackOrder = async (orderId, orderTrackId, customerNumber) => {
    if (!orderTrackId || !customerNumber) {
      setTrackingData(prev => ({
        ...prev,
        [orderId]: {
          trackingUrl: null,
          isTrackingAvailable: false,
          errorMessage: "Your Order is getting processed.",
        }
      }));
      return;
    }
  
    try {
      const response = await fetch(
        `https://apiv2.shiprocket.in/tracking-form-check?track_type=order_id&track_id=${orderTrackId}&cust_key=${customerNumber}`
      );
      const data = await response.json();
  
      if (response.status === 200 && data.url) {
        // 🔥 Fix escaped slashes
        const fixedTrackingUrl = data.url.replace(/\\/g, "");
  
        // ✅ Validate the URL by checking HTTP status
        const urlCheck = await fetch(fixedTrackingUrl, { method: "HEAD" });
  
        if (urlCheck.status === 400 || urlCheck.status === 404) {
          setTrackingData(prev => ({
            ...prev,
            [orderId]: {
              trackingUrl: null,
              isTrackingAvailable: false,
              errorMessage: "Your Order is getting processed.",
            }
          }));
        } else {
          setTrackingData(prev => ({
            ...prev,
            [orderId]: {
              trackingUrl: fixedTrackingUrl,
              isTrackingAvailable: true,
            }
          }));
        }
      } else {
        setTrackingData(prev => ({
          ...prev,
          [orderId]: {
            trackingUrl: null,
            isTrackingAvailable: false,
            errorMessage: "Your Order is getting processed.",
          }
        }));
      }
    } catch (error) {
      setTrackingData(prev => ({
        ...prev,
        [orderId]: {
          trackingUrl: null,
          isTrackingAvailable: false,
          errorMessage: "Failed to fetch tracking information",
        }
      }));
    }
  };


  const handleRateProduct = async (product) => {
    setSelectedProduct(product);
    console.log('Selected Product:', product);
    try {
      // Check if user has already rated this product
      const ratingResponse = await apiService.checkExistingRating(product.pr_id || product.id, customerId);
      if (ratingResponse.success && ratingResponse.data) {
        // Map API response fields to expected format
        const mappedRating = {
          rating: ratingResponse.data.ratingValue,
          review: ratingResponse.data.reviewText
        };
        setProductRatings(prev => ({
          ...prev,
          [product.pr_id || product.id]: mappedRating
        }));
      }
    } catch (error) {
      console.error("Error checking existing rating:", error);
    }
    
    setIsRatingModalOpen(true);
  };

  const handleRatingSubmit = async (ratingData) => {
    try {
      const payload = {
        product_id: selectedProduct.pr_id || selectedProduct.id,
        customer_id: customerId,
        rating: ratingData.rating,
        review: ratingData.review || ""
      };

      let response;
      const existingRating = productRatings[selectedProduct.pr_id || selectedProduct.id];
      
      if (existingRating) {
        response = await apiService.updateRating(payload);
      } else {
        response = await apiService.addRating(payload);
      }

      if (response.success) {
        toast.success(existingRating ? "Rating updated successfully" : "Rating added successfully");
        setProductRatings(prev => ({
          ...prev,
          [selectedProduct.pr_id || selectedProduct.id]: {
            rating: ratingData.rating,
            review: ratingData.review
          }
        }));
      } else {
        toast.error("Failed to submit rating");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      toast.error("Failed to submit rating");
    }
  };

  const closeRatingModal = () => {
    setIsRatingModalOpen(false);
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Loading Your Orders</h2>
          <p className="text-gray-500">Please wait while we fetch your order history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-3 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}>
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            Your Orders
          </h1>
          <p className="text-gray-600 text-sm">Track and manage your purchases</p>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-8">
            <div className="bg-white rounded-lg shadow-md p-6 max-w-sm mx-auto border border-gray-200">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No Orders Yet</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">Start shopping to see your orders here!</p>
              <button className="px-4 py-2 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300" style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}>
                Start Shopping
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                {/* Order Header - Professional Design */}
                <div className="p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}>
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base sm:text-lg font-bold text-gray-900">
                          Order #{order.order_id}
                        </h2>
                        <p className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold" style={{ color: 'black' }}>₹{order.total_amount}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Confirmed
                      </span>
                    </div>
                  </div>

                  {order.order_track_id && (
                    <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-xs text-gray-600 font-medium">Tracking ID:</span>
                        <span className="text-xs text-gray-900 font-semibold">{order.order_track_id}</span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons - Professional Design */}
                  <div className="flex gap-2">
                    <button
                      className="flex-1 px-3 py-2 text-white rounded-lg font-medium text-sm shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}
                      onClick={() => toggleDetails(order.id)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedOrderId === order.id ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                      </svg>
                      {expandedOrderId === order.id ? "Hide Details" : "View Details"}
                    </button>
                    <button
                      className="flex-1 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium text-sm shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                      onClick={() => setTrackingModalOrder(order)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Track Order
                    </button>
                  </div>
                </div>


                {/* Order Details - Professional Design */}
                {expandedOrderId === order.id && (
                  <div className="border-t border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}>
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900">Product Details</h3>
                    </div>
                    
                    {!order.order_data || order.order_data.length === 0 ? (
                      <div className="text-center py-4">
                        <div className="w-12 h-12 mx-auto mb-2 bg-gray-200 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">No Product Details Available</h4>
                        <p className="text-xs text-gray-600">Product information is not available for this order.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {order.order_data.map((product, index) => {
                        const isContactLens = Boolean(product.contact_lens_images);
                        const images = isContactLens
                          ? product.contact_lens_images?.split(",") || []
                          : product.pr_image?.split(",") || [];
                        const displayedImage = `${imageBaseURL}/${
                          images[product.selected_image_to_show || 0] || images[0] || ''
                        }`;

                        return (
                          <div key={index} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                            <div className="flex gap-3">
                              {/* Product Image */}
                              <div className="flex-shrink-0">
                                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-50 p-1">
                                  <img
                                    src={displayedImage}
                                    alt={product.pr_name || "Product"}
                                    className="w-full h-full object-cover rounded"
                                    onError={(e) => {
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTA4LjI4NCA3MCA5NS4wMDAwIDc1IDk1IDgzLjMzMzNWMTE2LjY2N0M5NSAxMjUgMTA4LjI4NCAxMzAgMTAwIDEzMEM5MS43MTU3IDEzMCA3OC4zMzMzIDEyNSA3OC4zMzMzIDExNi42NjdWODMuMzMzM0M3OC4zMzMzIDc1IDkxLjcxNTcgNzAgMTAwIDcwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Product Info */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2">
                                  {product.pr_name || "Product Name Unavailable"}
                                </h4>
                                
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-bold" style={{ color: 'black' }}>₹{product.price}</span>
                                </div>
                                
                                {(product.lens_material || product.lens_package_name) && (
                                  <div className="space-y-1 mb-2">
                                    {product.lens_material && (
                                      <div className="flex items-center gap-1 text-xs text-gray-600">
                                        <span className="font-medium">Lens:</span>
                                        <span>{product.lens_material}</span>
                                      </div>
                                    )}
                                    {product.lens_package_name && (
                                      <div className="flex items-center gap-1 text-xs text-gray-600">
                                        <span className="font-medium">Package:</span>
                                        <span>{product.lens_package_name}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Rating Section */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1">
                                    {productRatings[product.pr_id || product.id] ? (
                                      <div className="flex items-center gap-1">
                                        <StarRating 
                                          rating={productRatings[product.pr_id || product.id].rating} 
                                          readonly={true} 
                                          size={12}
                                        />
                                        <span className="text-xs text-gray-600">
                                          ({productRatings[product.pr_id || product.id].rating}/5)
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">Not rated</span>
                                    )}
                                  </div>
                                  
                                  <button
                                    onClick={() => handleRateProduct(product)}
                                    className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition-all duration-300 flex items-center gap-1"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                    Rate
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>
                )}


              </div>
            ))}
          </div>
        )}

        {/* Tracking Modal */}
        {trackingModalOrder && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="p-4 border-b border-gray-200" style={{ background: 'linear-gradient(135deg, rgb(31, 80, 154), rgb(0, 0, 102))' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Track Order</h3>
                      <p className="text-white/90 text-sm">Order #{trackingModalOrder.order_id}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setTrackingModalOrder(null)}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300 flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4">
                {trackingData[trackingModalOrder.id] ? (
                  trackingData[trackingModalOrder.id].isTrackingAvailable && trackingData[trackingModalOrder.id].trackingUrl ? (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="p-3 bg-yellow-500">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-white font-medium">Live Tracking Information</span>
                        </div>
                      </div>
                      <iframe
                        src={trackingData[trackingModalOrder.id].trackingUrl}
                        className="w-full h-[400px] border-0"
                        title="Order Tracking"
                        frameBorder="0"
                      />
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Processing Your Order</h4>
                      <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                        {trackingData[trackingModalOrder.id].errorMessage || "Your order is being prepared and tracking information will be available soon."}
                      </p>
                      <div className="mt-4 inline-flex items-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Order in Progress
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Loading Tracking Information</h4>
                    <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                      Please wait while we fetch your order tracking details.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rating Modal */}
        {isRatingModalOpen && selectedProduct && (
          <RatingModal
            isOpen={isRatingModalOpen}
            onClose={closeRatingModal}
            onSubmit={handleRatingSubmit}
            product={selectedProduct}
            existingRating={productRatings[selectedProduct.pr_id || selectedProduct.id]}
          />
        )}
      </div>
    </div>
  );
};

export default OrdersList;
